import {
  Activity,
  ArrowRight,
  BarChart3,
  Check,
  ChevronDown,
  CircleDollarSign,
  Clipboard,
  Code2,
  CreditCard,
  Database,
  ExternalLink,
  Eye,
  Globe2,
  LayoutDashboard,
  LoaderCircle,
  LockKeyhole,
  LogOut,
  Menu,
  MousePointer2,
  Plus,
  Radio,
  RefreshCw,
  Search,
  Settings2,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
  X,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { api, ApiError } from './api';
import { buildImplementationPrompt } from './implementation-prompt';

interface Site {
  id: string;
  name: string;
  domain: string;
  allowed_origins: string[];
  timezone: string;
  currency: string;
  currency_locked: boolean;
  public_key: string;
  stripe_connected: boolean;
  created_at: string;
  updated_at: string;
}

interface BreakdownRow {
  key: string;
  visitors: number;
  sessions: number;
  revenue: number;
  customers: number;
  conversion_rate: number;
  revenue_per_visitor: number;
}

interface Overview {
  range: { from: string; to: string };
  metrics: {
    visitors: number;
    sessions: number;
    goals: number;
    customers: number;
    payments: number;
    revenue: number;
    conversion_rate: number;
    revenue_per_visitor: number;
    attribution_coverage: number;
  };
  trend: Array<{ day: string; visitors: number; sessions: number; revenue: number }>;
  sources: BreakdownRow[];
  landing_pages: BreakdownRow[];
  freshness: string;
  model: { id: string; version: number; lookback_days: number };
}

interface Payment {
  id: string;
  provider: string;
  provider_object_id: string;
  occurred_at: string;
  amount_minor: number;
  currency: string;
  status: string;
  customer_id: string | null;
  anonymous_id: string | null;
  description: string | null;
  source: string | null;
  channel: string | null;
  campaign: string | null;
  landing_page: string | null;
  confidence: string | null;
}

interface EventItem {
  event_id: string;
  type: string;
  occurred_at: string;
  anonymous_id: string;
  session_id: string | null;
  path: string | null;
  title: string | null;
  source: string;
  channel: string;
  country: string | null;
  device: string | null;
  consent: string;
  quality_flags: string;
}

type View = 'overview' | 'payments' | 'events' | 'setup';

const NAVIGATION: Array<{ id: View; label: string; icon: LucideIcon }> = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'payments', label: 'Revenue', icon: CircleDollarSign },
  { id: 'events', label: 'Live events', icon: Radio },
  { id: 'setup', label: 'Project setup', icon: Settings2 },
];

const CURRENCY_OPTIONS = (() => {
  try {
    return Intl.supportedValuesOf('currency');
  } catch {
    return ['AUD', 'BGN', 'CAD', 'CHF', 'EUR', 'GBP', 'JPY', 'USD'];
  }
})();

const TIMEZONE_OPTIONS = (() => {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return ['America/New_York', 'Europe/London', 'Europe/Sofia', 'UTC'];
  }
})();

function formatMoney(amountMinor: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en', { style: 'currency', currency, maximumFractionDigits: 2 }).format(amountMinor / 100);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en', { notation: value >= 10_000 ? 'compact' : 'standard', maximumFractionDigits: 1 }).format(value);
}

function formatPercent(value: number): string {
  return new Intl.NumberFormat('en', { style: 'percent', maximumFractionDigits: 1 }).format(value);
}

function formatRelative(value: string): string {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`;
  return new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' }).format(new Date(value));
}

function compactId(value: string | null | undefined): string {
  if (!value) return '—';
  return value.length > 16 ? `${value.slice(0, 7)}…${value.slice(-5)}` : value;
}

function LoadingScreen() {
  return (
    <div className="center-screen">
      <div className="loader-mark"><span /><span /><span /></div>
      <p>Warming up the machine</p>
    </div>
  );
}

function Login({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [username, setUsername] = useState('datamaster');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api('/api/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) });
      onAuthenticated();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not sign in.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="login-shell">
      <section className="login-story">
        <div className="brand brand-light"><LogoMark /> <span>DataMachine</span></div>
        <div className="login-copy">
          <p className="eyebrow eyebrow-light"><Sparkles size={14} /> PRIVATE REVENUE INTELLIGENCE</p>
          <h1>Know what<br />actually makes<br /><em>money.</em></h1>
          <p>One calm, trustworthy view across every project in your portfolio. No vanity metrics. No mystery attribution.</p>
        </div>
        <div className="login-proof">
          <span><ShieldCheck size={17} /> Self-hosted</span>
          <span><Database size={17} /> Your data</span>
          <span><Eye size={17} /> Evidence first</span>
        </div>
      </section>
      <section className="login-panel">
        <form className="login-card" onSubmit={submit}>
          <div className="mobile-brand brand"><LogoMark /> <span>DataMachine</span></div>
          <div className="login-icon"><LockKeyhole size={23} /></div>
          <p className="eyebrow">PORTFOLIO CONSOLE</p>
          <h2>Welcome back</h2>
          <p className="muted">Sign in with the private administrator credentials for this installation.</p>
          <div className="login-fields">
            <label>
              Username
              <input
                autoFocus
                autoCapitalize="none"
                autoComplete="username"
                spellCheck={false}
                type="text"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                placeholder="datamaster"
              />
            </label>
            <label>
              Password
              <input
                autoComplete="current-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="••••••••••••"
              />
            </label>
          </div>
          {error && <div className="form-error">{error}</div>}
          <button className="button button-primary button-wide" disabled={loading || username.length < 1 || password.length < 1}>
            {loading ? <LoaderCircle className="spin" size={18} /> : <>Enter dashboard <ArrowRight size={18} /></>}
          </button>
          <p className="login-note"><LockKeyhole size={13} /> Session protected with an HttpOnly signed cookie</p>
        </form>
      </section>
    </main>
  );
}

function LogoMark() {
  return <span className="logo-mark"><i /><i /><i /></span>;
}

function EmptyPortfolio({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="empty-portfolio">
      <div className="orbit-art" aria-hidden="true">
        <span className="orbit orbit-a" /><span className="orbit orbit-b" />
        <span className="orbit-core"><BarChart3 size={34} /></span>
        <i className="dot dot-a" /><i className="dot dot-b" /><i className="dot dot-c" />
      </div>
      <p className="eyebrow">YOUR PORTFOLIO STARTS HERE</p>
      <h2>Connect the first project.</h2>
      <p>Add its domain, install one tiny script, then send a real or test payment. You’ll see the complete source → visit → revenue trail.</p>
      <button className="button button-primary" onClick={onCreate}><Plus size={18} /> Add first project</button>
      <div className="empty-steps">
        <span><b>01</b> Create project</span><ArrowRight size={16} />
        <span><b>02</b> Add website data</span><ArrowRight size={16} />
        <span><b>03</b> Connect revenue</span>
      </div>
    </div>
  );
}

function CreateProjectModal({ onClose, onCreated }: { onClose: () => void; onCreated: (site: Site) => void }) {
  const [step, setStep] = useState<'form' | 'credentials'>('form');
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [site, setSite] = useState<Site | null>(null);
  const [secret, setSecret] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const origin = useMemo(() => {
    try {
      return new URL(/^https?:\/\//iu.test(domain) ? domain : `https://${domain}`).origin;
    } catch {
      return '';
    }
  }, [domain]);

  async function create(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await api<{ site: Site; secret: string }>('/api/sites', {
        method: 'POST',
        body: JSON.stringify({ name, domain, allowed_origins: [origin], timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, currency }),
      });
      setSite(response.site);
      setSecret(response.secret);
      setStep('credentials');
      onCreated(response.site);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not create the project.');
    } finally {
      setLoading(false);
    }
  }

  const snippet = site ? `<script async src="${location.origin}/v1/data.js?key=${site.public_key}" data-site-key="${site.public_key}"></script>` : '';

  return (
    <Modal onClose={onClose} wide>
      {step === 'form' ? (
        <form onSubmit={create}>
          <div className="modal-heading">
            <span className="modal-icon"><Globe2 size={21} /></span>
            <div><p className="eyebrow">NEW PROJECT</p><h2>What are we measuring?</h2></div>
          </div>
          <div className="form-grid">
            <label>Project name<input required value={name} onChange={(event) => setName(event.target.value)} placeholder="Atlas CRM" /></label>
            <label>Primary domain<input required value={domain} onChange={(event) => setDomain(event.target.value)} placeholder="atlas.example.com" /></label>
            <label>Reporting currency
              <select value={currency} onChange={(event) => setCurrency(event.target.value)}>
                <option>USD</option><option>EUR</option><option>GBP</option><option>BGN</option><option>CAD</option><option>AUD</option>
              </select>
            </label>
            <div className="origin-preview"><span>Exact allowed origin</span><code>{origin || 'Enter a valid domain'}</code></div>
          </div>
          <div className="privacy-callout"><ShieldCheck size={19} /><div><b>Privacy-safe by default</b><span>Raw IP addresses are never stored. Sensitive URL parameters are redacted before persistence.</span></div></div>
          {error && <div className="form-error">{error}</div>}
          <div className="modal-actions"><button type="button" className="button button-ghost" onClick={onClose}>Cancel</button><button className="button button-primary" disabled={loading || !origin}>{loading ? <LoaderCircle className="spin" size={18} /> : <>Create project <ArrowRight size={18} /></>}</button></div>
        </form>
      ) : site ? (
        <div>
          <div className="success-badge"><Check size={23} /></div>
          <p className="eyebrow center">PROJECT READY</p>
          <h2 className="center">Install DataMachine on {site.name}</h2>
          <p className="muted center narrow">Paste this before the closing <code>&lt;/head&gt;</code> tag. The secret below is shown only once.</p>
          <CopyField label="Website data script" value={snippet} multiline />
          <CopyField label="Server payment secret" value={secret} />
          <div className="secret-warning"><LockKeyhole size={16} /> Store the server secret in your project’s secret manager—not in browser code.</div>
          <div className="modal-actions"><button className="button button-primary button-wide" onClick={onClose}>Open project <ArrowRight size={18} /></button></div>
        </div>
      ) : null}
    </Modal>
  );
}

function Modal({ children, onClose, wide = false }: { children: ReactNode; onClose: () => void; wide?: boolean }) {
  useEffect(() => {
    const listener = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    addEventListener('keydown', listener);
    return () => removeEventListener('keydown', listener);
  }, [onClose]);
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className={`modal ${wide ? 'modal-wide' : ''}`} role="dialog" aria-modal="true">
        <button className="icon-button modal-close" onClick={onClose} aria-label="Close dialog"><X size={19} /></button>
        {children}
      </section>
    </div>
  );
}

function CopyField({ label, value, multiline = false }: { label: string; value: string; multiline?: boolean }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(timer);
  }, [copied]);
  async function copy() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
  }
  return (
    <div className="copy-field">
      <span>{label}</span>
      <div className={multiline ? 'multiline' : ''}><code>{value}</code><button onClick={copy} type="button" aria-label={`Copy ${label}`}>{copied ? <Check size={16} /> : <Clipboard size={16} />} {copied ? 'Copied' : 'Copy'}</button></div>
    </div>
  );
}

function AgentPromptCard({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2200);
    return () => clearTimeout(timer);
  }, [copied]);

  async function copy() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
  }

  return (
    <article className="panel agent-prompt-card">
      <div className="agent-prompt-heading">
        <span className="agent-prompt-icon"><Sparkles size={21} /></span>
        <div><p className="eyebrow">IMPLEMENT WITH AI</p><h2>Hand this project to a coding agent</h2><p>One project-specific prompt with the installation, privacy, revenue, security, and verification contract.</p></div>
        <button className="button button-primary" type="button" onClick={() => void copy()}>{copied ? <Check size={17} /> : <Clipboard size={17} />}{copied ? 'Prompt copied' : 'Copy implementation prompt'}</button>
      </div>
      <pre className="agent-prompt-preview">{value}</pre>
      <div className="agent-prompt-note"><LockKeyhole size={15} /><span>The server payment secret is intentionally excluded. Give agents access through your project’s secret manager, never through source code or chat.</span></div>
    </article>
  );
}

function MetricCard({ label, value, detail, icon: Icon, tone = 'neutral' }: { label: string; value: string; detail: string; icon: LucideIcon; tone?: 'neutral' | 'green' | 'coral' | 'blue' }) {
  return (
    <article className={`metric-card tone-${tone}`}>
      <div className="metric-top"><span>{label}</span><i><Icon size={18} /></i></div>
      <strong>{value}</strong>
      <p>{detail}</p>
    </article>
  );
}

function RevenueChart({ data, currency }: { data: Overview['trend']; currency: string }) {
  const width = 820;
  const height = 240;
  const padding = 20;
  const max = Math.max(...data.map((point) => point.revenue), 100);
  const points = data.map((point, index) => ({
    x: data.length <= 1 ? width / 2 : padding + (index / (data.length - 1)) * (width - padding * 2),
    y: height - padding - (point.revenue / max) * (height - padding * 2),
    ...point,
  }));
  const line = points.map((point) => `${point.x},${point.y}`).join(' ');
  const area = points.length ? `${padding},${height - padding} ${line} ${width - padding},${height - padding}` : '';
  return (
    <div className="chart-wrap">
      {points.length ? (
        <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Revenue by day">
          <defs><linearGradient id="revenueFill" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="#b8f236" stopOpacity=".34" /><stop offset="100%" stopColor="#b8f236" stopOpacity="0" /></linearGradient></defs>
          {[0.25, 0.5, 0.75].map((ratio) => <line key={ratio} x1={padding} x2={width - padding} y1={height * ratio} y2={height * ratio} className="grid-line" />)}
          <polygon points={area} fill="url(#revenueFill)" />
          <polyline points={line} fill="none" className="revenue-line" />
          {points.map((point) => <circle key={point.day} cx={point.x} cy={point.y} r="4" className="chart-dot"><title>{`${point.day}: ${formatMoney(point.revenue, currency)}`}</title></circle>)}
        </svg>
      ) : (
        <div className="chart-empty"><BarChart3 size={28} /><span>Revenue will appear after the first payment</span></div>
      )}
      <div className="chart-labels"><span>{data[0]?.day ? new Date(`${data[0].day}T00:00:00Z`).toLocaleDateString('en', { month: 'short', day: 'numeric' }) : 'Start'}</span><span>{data.at(-1)?.day ? new Date(`${data.at(-1)?.day}T00:00:00Z`).toLocaleDateString('en', { month: 'short', day: 'numeric' }) : 'Today'}</span></div>
    </div>
  );
}

function BreakdownTable({ rows, currency, mode }: { rows: BreakdownRow[]; currency: string; mode: 'source' | 'page' }) {
  const maxRevenue = Math.max(...rows.map((row) => Math.abs(row.revenue)), 1);
  return (
    <div className="data-table-wrap">
      <table className="data-table">
        <thead><tr><th>{mode === 'source' ? 'Source' : 'Landing page'}</th><th>Visitors</th><th>Customers</th><th>Conv.</th><th>Revenue</th><th>RPV</th></tr></thead>
        <tbody>
          {rows.length ? rows.slice(0, 10).map((row, index) => (
            <tr key={row.key}>
              <td><div className="row-key"><span className={`source-dot source-${index % 5}`} /> <b>{row.key}</b></div><div className="row-bar"><i style={{ width: `${Math.max(3, Math.abs(row.revenue) / maxRevenue * 100)}%` }} /></div></td>
              <td>{formatNumber(row.visitors)}</td><td>{formatNumber(row.customers)}</td><td>{formatPercent(row.conversion_rate)}</td><td className="money-cell">{formatMoney(row.revenue, currency)}</td><td>{formatMoney(row.revenue_per_visitor, currency)}</td>
            </tr>
          )) : <tr><td colSpan={6}><TableEmpty label={mode === 'source' ? 'No source data yet' : 'No landing-page data yet'} /></td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function TableEmpty({ label }: { label: string }) {
  return <div className="table-empty"><Activity size={18} /><span>{label}</span></div>;
}

function OverviewView({ site, overview, loading, onRefresh }: { site: Site; overview: Overview | null; loading: boolean; onRefresh: () => void }) {
  if (loading && !overview) return <ViewLoader />;
  const metrics = overview?.metrics;
  return (
    <div className="view-stack">
      <header className="page-header">
        <div><p className="eyebrow"><span className="live-dot" /> LIVE PORTFOLIO DATA</p><h1>Revenue overview</h1><p>What created money for <b>{site.name}</b> in the last 30 days.</p></div>
        <div className="header-actions"><button className="button button-secondary" onClick={onRefresh} disabled={loading}><RefreshCw className={loading ? 'spin' : ''} size={16} /> Refresh</button><button className="date-button">Last 30 days <ChevronDown size={16} /></button></div>
      </header>
      <div className="trust-strip">
        <span><ShieldCheck size={16} /> Model: last non-direct v{overview?.model.version ?? 1}</span>
        <span><Activity size={16} /> Fresh {overview ? formatRelative(overview.freshness) : 'now'}</span>
        <span><Eye size={16} /> Coverage {formatPercent(metrics?.attribution_coverage ?? 0)}</span>
      </div>
      <section className="metrics-grid">
        <MetricCard label="Net revenue" value={formatMoney(metrics?.revenue ?? 0, site.currency)} detail={`${metrics?.payments ?? 0} payment facts`} icon={CircleDollarSign} tone="green" />
        <MetricCard label="Visitors" value={formatNumber(metrics?.visitors ?? 0)} detail={`${formatNumber(metrics?.sessions ?? 0)} sessions`} icon={Users} tone="blue" />
        <MetricCard label="Customers" value={formatNumber(metrics?.customers ?? 0)} detail={`${formatPercent(metrics?.conversion_rate ?? 0)} conversion`} icon={Target} tone="coral" />
        <MetricCard label="Revenue / visitor" value={formatMoney(metrics?.revenue_per_visitor ?? 0, site.currency)} detail={`${metrics?.goals ?? 0} tracked goals`} icon={Zap} />
      </section>
      <section className="panel chart-panel">
        <div className="panel-heading"><div><p className="eyebrow">REVENUE MOMENTUM</p><h2>Revenue over time</h2></div><div className="legend"><i /> Net revenue</div></div>
        <RevenueChart data={overview?.trend ?? []} currency={site.currency} />
      </section>
      <section className="panel">
        <div className="panel-heading"><div><p className="eyebrow">ACQUISITION</p><h2>Revenue by source</h2></div><span className="panel-caption">Every row reconciles to the visible total</span></div>
        <BreakdownTable rows={overview?.sources ?? []} currency={site.currency} mode="source" />
      </section>
      <section className="panel">
        <div className="panel-heading"><div><p className="eyebrow">ENTRY POINTS</p><h2>Landing pages</h2></div><span className="panel-caption">Where paying journeys began</span></div>
        <BreakdownTable rows={overview?.landing_pages ?? []} currency={site.currency} mode="page" />
      </section>
    </div>
  );
}

function PaymentsView({ site }: { site: Site }) {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Record<string, unknown> | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api<{ payments: Payment[] }>(`/api/sites/${site.id}/payments`);
      setPayments(response.payments);
    } finally { setLoading(false); }
  }, [site.id]);
  useEffect(() => { void load(); }, [load]);

  async function inspect(payment: Payment) {
    const response = await api<{ payment: Record<string, unknown> }>(`/api/sites/${site.id}/payments/${payment.id}`);
    setSelected(response.payment);
  }

  return (
    <div className="view-stack">
      <header className="page-header"><div><p className="eyebrow">COMMERCIAL TRUTH</p><h1>Revenue evidence</h1><p>Every payment is traceable to its matching visitor and acquisition touch.</p></div><button className="button button-secondary" onClick={() => void load()}><RefreshCw size={16} /> Refresh</button></header>
      <section className="panel">
        <div className="panel-heading"><div><p className="eyebrow">PAYMENT LEDGER</p><h2>Recent payments</h2></div><span className="count-pill">{payments.length} records</span></div>
        {loading ? <ViewLoader compact /> : (
          <div className="data-table-wrap">
            <table className="data-table payment-table">
              <thead><tr><th>Payment</th><th>When</th><th>Source</th><th>Status</th><th>Amount</th><th /></tr></thead>
              <tbody>{payments.length ? payments.map((payment) => (
                <tr key={payment.id}>
                  <td><b>{payment.description || compactId(payment.provider_object_id)}</b><small>{payment.provider} · {compactId(payment.provider_object_id)}</small></td>
                  <td>{formatRelative(payment.occurred_at)}</td>
                  <td><span className="source-chip">{payment.source || 'Unattributed'}</span><small>{payment.channel || 'No matching visit'}</small></td>
                  <td><span className={`status status-${payment.status}`}>{payment.status}</span></td>
                  <td className={payment.amount_minor < 0 ? 'negative money-cell' : 'money-cell'}>{formatMoney(payment.amount_minor, payment.currency)}</td>
                  <td><button className="text-button" onClick={() => void inspect(payment)}>Evidence <ArrowRight size={14} /></button></td>
                </tr>
              )) : <tr><td colSpan={6}><TableEmpty label="Payments appear here after a generic API call or Stripe webhook" /></td></tr>}</tbody>
            </table>
          </div>
        )}
      </section>
      {selected && <EvidenceDrawer payment={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function EvidenceDrawer({ payment, onClose }: { payment: Record<string, unknown>; onClose: () => void }) {
  const events = Array.isArray(payment.events) ? payment.events as Array<Record<string, unknown>> : [];
  const attributedId = typeof payment.attributed_event_id === 'string' ? payment.attributed_event_id : null;
  return (
    <div className="drawer-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <aside className="drawer">
        <div className="drawer-top"><div><p className="eyebrow">ATTRIBUTION EVIDENCE</p><h2>{formatMoney(Number(payment.amount_minor ?? 0), String(payment.currency ?? 'USD'))}</h2></div><button className="icon-button" onClick={onClose} aria-label="Close evidence"><X size={19} /></button></div>
        <div className="evidence-verdict"><ShieldCheck size={22} /><div><b>{String(payment.source ?? 'Unattributed')}</b><span>{String(payment.channel ?? 'No eligible visit')} · {String(payment.confidence ?? 'none')} confidence</span></div></div>
        <div className="definition-list">
          <span>Payment ID</span><code>{compactId(String(payment.provider_object_id ?? ''))}</code>
          <span>Model</span><b>Last non-direct · v{String(payment.model_version ?? 1)}</b>
          <span>Match</span><b>{String(payment.match_method ?? 'unmatched').replaceAll('_', ' ')}</b>
          <span>Computed</span><b>{typeof payment.computed_at === 'string' ? formatRelative(payment.computed_at) : '—'}</b>
        </div>
        <h3>Journey evidence</h3>
        <div className="timeline">
          {events.length ? events.sort((left, right) => String(left.occurred_at).localeCompare(String(right.occurred_at))).map((event) => (
            <div className={event.event_id === attributedId ? 'timeline-item attributed' : 'timeline-item'} key={String(event.event_id)}>
              <i>{event.event_id === attributedId ? <Check size={13} /> : <MousePointer2 size={13} />}</i>
              <div><b>{String(event.path ?? '/')}</b><span>{String(event.source ?? 'Direct')} · {String(event.channel ?? 'Direct')}</span><small>{typeof event.occurred_at === 'string' ? new Date(event.occurred_at).toLocaleString() : ''}</small></div>
              {event.event_id === attributedId && <em>Attributed</em>}
            </div>
          )) : <div className="no-evidence"><Search size={22} /><b>No eligible visit found</b><span>Include <code>dm_anonymous_id</code> in Stripe metadata or the generic payment call.</span></div>}
          <div className="timeline-item payment-point"><i><CreditCard size={13} /></i><div><b>Payment succeeded</b><span>{compactId(String(payment.provider_object_id ?? ''))}</span></div></div>
        </div>
      </aside>
    </div>
  );
}

function EventsView({ site }: { site: Site }) {
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { setEvents((await api<{ events: EventItem[] }>(`/api/sites/${site.id}/events?limit=100`)).events); }
    finally { setLoading(false); }
  }, [site.id]);
  useEffect(() => { void load(); const timer = setInterval(() => void load(), 10_000); return () => clearInterval(timer); }, [load]);
  return (
    <div className="view-stack">
      <header className="page-header"><div><p className="eyebrow"><span className="live-dot" /> AUTO-REFRESHING</p><h1>Live event stream</h1><p>Newest accepted events from {site.name}. Refreshes every ten seconds.</p></div><button className="button button-secondary" onClick={() => void load()}><RefreshCw className={loading ? 'spin' : ''} size={16} /> Refresh</button></header>
      <section className="panel event-panel">
        <div className="event-head"><span>Event</span><span>Acquisition</span><span>Context</span><span>Received</span></div>
        {loading && !events.length ? <ViewLoader compact /> : events.length ? events.map((item) => (
          <article className="event-row" key={item.event_id}>
            <div><i className={`event-type type-${item.type}`}><EventIcon type={item.type} /></i><span><b>{item.type.replaceAll('_', ' ')}</b><code>{compactId(item.event_id)}</code></span></div>
            <div><b>{item.source}</b><span>{item.channel}</span></div>
            <div><b>{item.path || '—'}</b><span>{[item.country, item.device].filter(Boolean).join(' · ') || 'Unknown context'}</span></div>
            <time>{formatRelative(item.occurred_at)}</time>
          </article>
        )) : <TableEmpty label="Waiting for the first verified event" />}
      </section>
    </div>
  );
}

function EventIcon({ type }: { type: string }) {
  if (type === 'goal') return <Target size={15} />;
  if (type === 'identify') return <Users size={15} />;
  if (type === 'install_check') return <Check size={15} />;
  return <MousePointer2 size={15} />;
}

function ProjectDetailsCard({ site, onUpdated }: { site: Site; onUpdated: (site: Site) => void }) {
  const [name, setName] = useState(site.name);
  const [domain, setDomain] = useState(site.domain);
  const [currency, setCurrency] = useState(site.currency);
  const [timezone, setTimezone] = useState(site.timezone);
  const [origins, setOrigins] = useState(site.allowed_origins.join('\n'));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const currencyOptions = useMemo(() => [...new Set([site.currency, ...CURRENCY_OPTIONS])], [site.currency]);
  const timezoneOptions = useMemo(() => [...new Set([site.timezone, ...TIMEZONE_OPTIONS])], [site.timezone]);
  const originValues = origins.split(/\r?\n/u).map((origin) => origin.trim()).filter(Boolean);
  const dirty = name.trim() !== site.name
    || domain.trim() !== site.domain
    || currency !== site.currency
    || timezone.trim() !== site.timezone
    || originValues.join('\n') !== site.allowed_origins.join('\n');

  function applySite(updated: Site) {
    setName(updated.name);
    setDomain(updated.domain);
    setCurrency(updated.currency);
    setTimezone(updated.timezone);
    setOrigins(updated.allowed_origins.join('\n'));
    onUpdated(updated);
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    setMessage('');
    setError('');
    if (!originValues.length) {
      setError('Add at least one exact browser origin, including https://.');
      return;
    }
    setSaving(true);
    try {
      const response = await api<{ site: Site }>(`/api/sites/${site.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: name.trim(), domain: domain.trim(), currency, timezone: timezone.trim(), allowed_origins: originValues }),
      });
      applySite(response.site);
      setMessage('Project details saved. The implementation prompt has been updated.');
    } catch (caught) {
      if (caught instanceof ApiError && (caught.code === 'currency_locked' || caught.code === 'site_update_conflict')) {
        try {
          const refreshed = await api<{ site: Site }>(`/api/sites/${site.id}`);
          applySite(refreshed.site);
        } catch {
          // Preserve the authoritative currency-lock error if refreshing the card fails.
        }
      }
      setError(caught instanceof Error ? caught.message : 'Could not save project details.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <article className="panel project-details-card">
      <div className="project-details-heading">
        <span className="project-details-icon"><Settings2 size={21} /></span>
        <div><p className="eyebrow">PROJECT CONFIGURATION</p><h2>Project details</h2><p>These values control browser access, reporting boundaries, and every generated implementation instruction.</p></div>
        <span className={site.currency_locked ? 'currency-state locked' : 'currency-state'}>{site.currency_locked ? <LockKeyhole size={14} /> : <Check size={14} />}{site.currency_locked ? `${site.currency} locked` : 'Currency editable'}</span>
      </div>
      <form className="project-details-form" onSubmit={save}>
        <div className="project-details-grid">
          <label>Project name<input required maxLength={80} value={name} onChange={(event) => setName(event.target.value)} /></label>
          <label>Primary domain<input required value={domain} onChange={(event) => setDomain(event.target.value)} placeholder="app.example.com" /></label>
          <label>Reporting currency
            <select value={currency} disabled={site.currency_locked} onChange={(event) => setCurrency(event.target.value)}>{currencyOptions.map((code) => <option key={code} value={code}>{code}</option>)}</select>
            <small>{site.currency_locked ? 'Locked because this project has payment records.' : 'Can be changed until the first payment is recorded.'}</small>
          </label>
          <label>Reporting timezone
            <input required list={`timezone-options-${site.id}`} value={timezone} onChange={(event) => setTimezone(event.target.value)} placeholder="Europe/Sofia" />
            <datalist id={`timezone-options-${site.id}`}>{timezoneOptions.map((value) => <option key={value} value={value} />)}</datalist>
          </label>
          <label className="origins-field">Allowed browser origins
            <textarea required rows={Math.max(3, Math.min(6, originValues.length + 1))} value={origins} onChange={(event) => setOrigins(event.target.value)} placeholder={'https://example.com\nhttps://www.example.com'} />
            <small>One exact origin per line. Include the scheme; paths are removed during normalization.</small>
          </label>
        </div>
        {site.currency_locked && <div className="currency-lock-note"><LockKeyhole size={16} /><span><b>Financial boundary protected.</b> Existing payments remain in {site.currency}. Other project details can still be edited safely.</span></div>}
        {error && <div className="form-error">{error}</div>}
        {message && <div className="form-success"><Check size={16} /> {message}</div>}
        <div className="project-details-actions"><button className="button button-primary" disabled={saving || !dirty || !name.trim() || !domain.trim() || !timezone.trim() || !originValues.length}>{saving ? <LoaderCircle className="spin" size={17} /> : <>Save project details <ArrowRight size={17} /></>}</button></div>
      </form>
    </article>
  );
}

function SetupView({ site, onUpdated }: { site: Site; onUpdated: (site: Site) => void }) {
  const [stripeSecret, setStripeSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [rotatedSecret, setRotatedSecret] = useState('');
  const dataScript = `<script async src="${location.origin}/v1/data.js?key=${site.public_key}" data-site-key="${site.public_key}"></script>`;
  const webhook = `${location.origin}/v1/webhooks/stripe/${site.public_key}`;
  const implementationPrompt = useMemo(() => buildImplementationPrompt({ site, baseUrl: location.origin }), [site]);

  async function connectStripe(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setNotice('');
    try {
      const response = await api<{ site: Site }>(`/api/sites/${site.id}`, { method: 'PATCH', body: JSON.stringify({ stripe_webhook_secret: stripeSecret }) });
      onUpdated(response.site);
      setStripeSecret('');
      setNotice('Stripe signing secret encrypted and saved.');
    } catch (caught) { setNotice(caught instanceof Error ? caught.message : 'Could not save Stripe configuration.'); }
    finally { setSaving(false); }
  }

  async function rotate() {
    if (!confirm('Rotate the server payment secret? Existing integrations will stop working immediately.')) return;
    const response = await api<{ secret: string; site: Site }>(`/api/sites/${site.id}/rotate-secret`, { method: 'POST' });
    setRotatedSecret(response.secret);
    onUpdated(response.site);
  }

  return (
    <div className="view-stack">
      <header className="page-header"><div><p className="eyebrow">INSTALL & OPERATE</p><h1>Project setup</h1><p>Website data, payment API, and Stripe connection for {site.name}.</p></div><a className="button button-secondary" href={`https://${site.domain}`} target="_blank" rel="noreferrer">Open site <ExternalLink size={15} /></a></header>
      <ProjectDetailsCard site={site} onUpdated={onUpdated} />
      <AgentPromptCard value={implementationPrompt} />
      <section className="setup-grid">
        <article className="panel setup-card"><div className="setup-number">01</div><Code2 size={24} /><h2>Add website data</h2><p>Paste the snippet in the global <code>&lt;head&gt;</code>. It records page views, SPA navigation, referrer, and UTM dimensions.</p><CopyField label="Data script snippet" value={dataScript} multiline /><div className="setup-status"><span className="status-dot amber" /> Waiting for live verification</div></article>
        <article className="panel setup-card"><div className="setup-number">02</div><CreditCard size={24} /><h2>Connect Stripe</h2><p>Create a Stripe webhook for the endpoint below, subscribe to checkout and payment events, then save its signing secret.</p><CopyField label="Webhook endpoint" value={webhook} multiline />
          <form className="inline-secret-form" onSubmit={connectStripe}><label>Webhook signing secret<input type="password" value={stripeSecret} onChange={(event) => setStripeSecret(event.target.value)} placeholder="whsec_••••••••" /></label><button className="button button-primary" disabled={saving || !stripeSecret}>{saving ? <LoaderCircle className="spin" size={17} /> : site.stripe_connected ? 'Update secret' : 'Connect Stripe'}</button></form>
          {site.stripe_connected && <div className="setup-status success"><span className="status-dot green" /> Stripe webhook secret configured</div>}
          {notice && <p className="form-notice">{notice}</p>}
        </article>
        <article className="panel setup-card"><div className="setup-number">03</div><Database size={24} /><h2>Send server payments</h2><p>For non-Stripe projects, call the generic payment endpoint with your site secret and an idempotency key.</p><CopyField label="Payment endpoint" value={`${location.origin}/v1/payments`} />
          <pre className="code-sample">{`curl -X POST ${location.origin}/v1/payments \\\n+  -H "Authorization: Bearer site_sec_…" \\\n+  -H "Idempotency-Key: order_123" \\\n+  -H "Content-Type: application/json" \\\n+  -d '{"provider_object_id":"order_123",\n       "occurred_at":"${new Date().toISOString()}",\n       "amount_minor":9900,"currency":"${site.currency}",\n       "anonymous_id":"01j…"}'`}</pre>
          <button className="text-button danger-link" onClick={() => void rotate()}><RefreshCw size={14} /> Rotate server secret</button>
          {rotatedSecret && <CopyField label="New secret — shown once" value={rotatedSecret} />}
        </article>
      </section>
    </div>
  );
}

function ViewLoader({ compact = false }: { compact?: boolean }) {
  return <div className={compact ? 'view-loader compact' : 'view-loader'}><LoaderCircle className="spin" size={23} /><span>Loading trusted data…</span></div>;
}

export function App() {
  const [booting, setBooting] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [sites, setSites] = useState<Site[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [view, setView] = useState<View>('overview');
  const [overview, setOverview] = useState<Overview | null>(null);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [globalError, setGlobalError] = useState('');

  const selectedSite = sites.find((site) => site.id === selectedSiteId) ?? sites[0] ?? null;

  const loadSites = useCallback(async () => {
    const response = await api<{ sites: Site[] }>('/api/sites');
    setSites(response.sites);
    setSelectedSiteId((current) => current && response.sites.some((site) => site.id === current) ? current : response.sites[0]?.id ?? null);
  }, []);

  const bootstrap = useCallback(async () => {
    setBooting(true);
    try {
      await api('/api/me');
      setAuthenticated(true);
      await loadSites();
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) setAuthenticated(false);
      else setGlobalError(error instanceof Error ? error.message : 'Could not start DataMachine.');
    } finally { setBooting(false); }
  }, [loadSites]);

  useEffect(() => { void bootstrap(); }, [bootstrap]);

  const loadOverview = useCallback(async () => {
    if (!selectedSite) return;
    setLoadingOverview(true);
    try {
      setOverview(await api<Overview>(`/api/sites/${selectedSite.id}/overview`));
    } catch (error) { setGlobalError(error instanceof Error ? error.message : 'Could not load dashboard data.'); }
    finally { setLoadingOverview(false); }
  }, [selectedSite]);

  useEffect(() => { setOverview(null); if (selectedSite) void loadOverview(); }, [selectedSite?.id, loadOverview]);

  async function logout() {
    await api('/api/auth/logout', { method: 'POST' });
    setAuthenticated(false);
    setSites([]);
  }

  function updateSite(updated: Site) {
    setSites((current) => current.map((site) => site.id === updated.id ? updated : site));
  }

  if (booting) return <LoadingScreen />;
  if (!authenticated) return <Login onAuthenticated={() => void bootstrap()} />;

  return (
    <div className="app-shell">
      <aside className={mobileMenu ? 'sidebar open' : 'sidebar'}>
        <div className="brand"><LogoMark /> <span>DataMachine</span></div>
        <button className="mobile-close icon-button" onClick={() => setMobileMenu(false)} aria-label="Close navigation"><X size={20} /></button>
        <div className="project-switcher">
          <label>ACTIVE PROJECT</label>
          <button onClick={() => sites.length === 0 && setCreateOpen(true)}>
            <span className="project-avatar">{selectedSite?.name.slice(0, 1).toUpperCase() ?? '+'}</span>
            <span><b>{selectedSite?.name ?? 'Add a project'}</b><small>{selectedSite?.domain ?? 'Portfolio is empty'}</small></span>
            {sites.length > 1 && <ChevronDown size={16} />}
          </button>
          {sites.length > 1 && <select aria-label="Active project" value={selectedSite?.id} onChange={(event) => setSelectedSiteId(event.target.value)}>{sites.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}</select>}
        </div>
        <nav>{NAVIGATION.map(({ id, label, icon: Icon }) => <button key={id} className={view === id ? 'active' : ''} disabled={!selectedSite} onClick={() => { setView(id); setMobileMenu(false); }}><Icon size={18} /> {label}{view === id && <ArrowRight size={15} />}</button>)}</nav>
        <div className="sidebar-spacer" />
        <div className="portfolio-meter"><div><span>Portfolio</span><b>{sites.length} / 20 projects</b></div><div className="meter"><i style={{ width: `${sites.length / 20 * 100}%` }} /></div><button onClick={() => setCreateOpen(true)} disabled={sites.length >= 20}><Plus size={15} /> Add project</button></div>
        <button className="logout-button" onClick={() => void logout()}><LogOut size={16} /> Sign out</button>
      </aside>
      {mobileMenu && <div className="sidebar-scrim" onClick={() => setMobileMenu(false)} />}
      <main className="main-content">
        <div className="mobile-topbar"><button className="icon-button" onClick={() => setMobileMenu(true)} aria-label="Open navigation"><Menu size={21} /></button><div className="brand"><LogoMark /> <span>DataMachine</span></div><button className="icon-button" onClick={() => setCreateOpen(true)} aria-label="Add project"><Plus size={20} /></button></div>
        {globalError && <div className="global-error"><span>{globalError}</span><button onClick={() => setGlobalError('')} aria-label="Dismiss error"><X size={16} /></button></div>}
        {!selectedSite ? <EmptyPortfolio onCreate={() => setCreateOpen(true)} /> : view === 'overview' ? <OverviewView site={selectedSite} overview={overview} loading={loadingOverview} onRefresh={() => void loadOverview()} /> : view === 'payments' ? <PaymentsView site={selectedSite} /> : view === 'events' ? <EventsView site={selectedSite} /> : <SetupView key={selectedSite.id} site={selectedSite} onUpdated={updateSite} />}
      </main>
      {createOpen && <CreateProjectModal onClose={() => setCreateOpen(false)} onCreated={(site) => { setSites((current) => [...current, site]); setSelectedSiteId(site.id); setView('setup'); }} />}
    </div>
  );
}
